import { safeLocalStorage } from 'wsm-tagging-utils';
import filterSearchClick, {
	DDC_INCENTIVE_FILTERSEARCH,
	DDC_INVENTORY_FILTERSEARCH_CLICK
} from '../../events/page/filterSearchClick';
import handleDynamicForms from './handleDynamicForms';
import getSelectorsToHandle from './selectorsToHandle';
import pushEvent from '../pushEvent';
import handleTrackEvent from './handleTrackEvent';
import handleDDCEvent from './handleDDCEvent';

// US2055082: The mobile hamburger slide-in's submenu group headers emit no
// ddc.link.generic on their own — the DDC header script that expands/collapses
// them stops the click's propagation, so ws-tracking's body-delegated (bubble)
// linkClick handler never sees them (only gtm.click fires). On slide-in open,
// install ONE capture-phase delegated click listener on the document (guarded so
// it binds a single time). Capture is essential and does three jobs at once:
//   1. it runs top-down before the toggle's own handlers, so it fires before the
//      propagation stop that blocks the bubble delegate;
//   2. it therefore reads the collapse panel while still in its PRE-toggle state;
//   3. as a document-level delegate it catches the ajax-rendered toggles whenever
//      they are clicked, with no render-timing race (per-element binding on open
//      missed the first click because the toggles render after slideOpen fires).
// Scope to `.ddc-mobile-slidein-content-inner` — the slide-in nav copy — so the
// always-present footer nav copy (identical markup) is never mislabeled. Emit via
// the existing (win-bound) clicked handler, marked additively with
// navContext:'sidebar' and navToggleState 'open'|'close'. Open vs close comes from
// the toggle's own collapse panel (`.panel-collapse`/`.collapse` within its
// `.panel`) carrying the `nav-visible` class: absent => this click opens, present
// => this click closes. The toggle's aria-expanded goes stale after the first
// cycle and the accordion ids are duplicated across the footer/slide-in copies, so
// neither is usable. Additive: only this path sets navContext/navToggleState; the
// shared clicked handler is unchanged.
const SIDEBAR_SUBMENU_TOGGLE =
	'.ddc-mobile-slidein-content-inner a.no-follow.panel-heading-link.panel-toggle';
let sidebarSubmenuCaptureBound = false;
const rebindSidebarSubmenuToggles = (win, link) => () => {
	if (sidebarSubmenuCaptureBound) {
		return;
	}
	sidebarSubmenuCaptureBound = true;
	document.addEventListener(
		'click',
		(event) => {
			const toggle = event.target?.closest?.(SIDEBAR_SUBMENU_TOGGLE);
			if (!toggle) {
				return;
			}
			// events.link.clicked is win-bound (handleEvents.js); synthesize the
			// event shape it reads (currentTarget/timeStamp/originalEvent).
			const eventObject = link.clicked({
				currentTarget: toggle,
				timeStamp: event.timeStamp,
				originalEvent: event
			});
			if (eventObject?.eventData) {
				const collapse = toggle
					.closest('.panel')
					?.querySelector('.panel-collapse, .collapse');
				const isOpen = Boolean(
					collapse?.classList?.contains('nav-visible')
				);
				eventObject.eventData.navContext = 'sidebar';
				eventObject.eventData.navToggleState = isOpen
					? 'close'
					: 'open';
				pushEvent(win, eventObject);
			}
		},
		true
	);
};

// TA4137176 (US2046694, SDG 5.4.2.11): a desktop nav megamenu dismissed WITHOUT
// clicking its own toggle — clicking anywhere outside it, or ESC while the
// toggle is focused — closes the dropdown but fired no link event at all, so
// the Adobe close beacon was never sent (only gtm.click). Bootstrap 3 fires
// `hide.bs.dropdown` on the `li.dropdown` for exactly those dismissals:
// verified ZERO occurrences when the toggle itself is re-clicked (that path
// already emits ddc.link.generic and is tagged from it), ZERO when a different
// menu is opened, and ZERO on any open — so this cannot double-count.
// `hidden.bs.dropdown` is unusable here: DDC's nav code triggers a spurious one
// on the menu that is about to OPEN. Emits a dedicated ddc.link.navToggle
// rather than a synthetic ddc.link.generic so no other tagging project's broad
// generic-link rules can pick it up.
export const DDC_LINK_NAVTOGGLE = 'ddc.link.navToggle';

const NAV_DROPDOWN_TOGGLE = 'a.nav-with-children[data-toggle="dropdown"]';

// Build the close event for a closing li.dropdown. The toggle anchor inside it
// carries the menu label. Scoped to the nav's own toggle class so the hidden
// bootstrap-select pickers (also data-toggle="dropdown") can never produce one.
const navDropdownCloseEvent = (li) => {
	const toggle = li?.querySelector?.(NAV_DROPDOWN_TOGGLE);

	return toggle
		? {
				eventType: DDC_LINK_NAVTOGGLE,
				eventData: {
					element: toggle,
					text: (toggle.textContent || '').trim(),
					navToggleState: 'close',
					position: 'topNav'
				}
		  }
		: undefined;
};

// Bootstrap triggers hide.bs.dropdown on the li.dropdown being dismissed.
const navDropdownHide = ({ target } = {}) => navDropdownCloseEvent(target);

// Opening a DIFFERENT megamenu silently closes the one already open: bootstrap
// fires no hide/hidden on that closing menu (verified 3/3 runs), so its close
// went untagged too — the same defect as an outside click. At
// show.bs.dropdown the closing menu still carries `.open` while the opening one
// does not yet, so the menu being closed is unambiguous here, and this runs
// synchronously inside the same user gesture (a MutationObserver would be async
// and would need extra bookkeeping to tell this path from the others).
// Bootstrap keeps at most one menu open, so there is only ever one to close.
const navDropdownShow = ({ target } = {}) => {
	const openingLi = target?.closest?.('li.dropdown') || target;
	const closingLi = Array.from(
		document.querySelectorAll('li.dropdown.open')
	).find((li) => li !== openingLi);

	return navDropdownCloseEvent(closingLi);
};

/*
	This section is for handling:
	$('body').on(event,selectors[selector],handler);
*/
const getDocumentOnHandlers = (
	win,
	{ dialog, form, link, page, slideshow }
) => ({
	click: [
		{
			selectorKey: 'formInitiation',
			handler: form.initiation
		},
		{
			selectorKey: 'formPaymentCalc',
			handler: form.paymentCalcInit
		},
		{
			selectorKey: 'formSubmission',
			handler: form.submission
		},
		{
			selectorKey: 'clickToCall',
			handler: link.clickToCall
		},
		{
			selectorKey: 'compareId',
			handler: link.compare
		},
		{
			selectorKey: 'incentiveId',
			handler: link.incentive
		},
		{
			selectorKey: 'getDirections',
			handler: link.getDirections
		},
		{
			selectorKey: 'linkClick',
			handler: link.clicked
		},
		{
			selectorKey: 'incentiveLabel',
			handler: link.clicked
		},
		{
			selectorKey: 'linksShareId',
			handler: link.share
		},
		{
			selectorKey: 'navigationId',
			handler: link.navigationClick
		},
		{
			selectorKey: 'vehicleCompare',
			handler: link.vehicleCompare
		},
		{
			selectorKey: 'slideType',
			handler: slideshow.click
		},
		{
			selectorKey: 'socialHeaderButtonId',
			handler: link.socialHeader
		},
		{
			selectorKey: 'vin',
			handler: link.hproduct
		},
		{
			selectorKey: 'typedSearchInit',
			handler: page.typedSearchInit
		},
		{
			// When global incentives facets are used
			selectorKey: 'incentiveFacet',
			handler: filterSearchClick.bind(
				undefined,
				DDC_INCENTIVE_FILTERSEARCH
			)
		},
		{
			// When vehicle search facets are used
			selectorKey: 'searchFacet',
			handler: filterSearchClick.bind(
				undefined,
				DDC_INVENTORY_FILTERSEARCH_CLICK
			)
		},
		{
			selectorKey: 'dialogClose',
			handler: dialog.closed
		},
		{
			selectorKey: 'dialogBackdropClose',
			handler: dialog.closed
		},
		{
			selectorKey: 'dialogCloseFilters',
			handler: dialog.closed
		}
	],
	mousedown: [
		{
			selectorKey: 'linkClick',
			handler: link.centerClicked
		}
	],
	input: [
		{
			selectorKey: 'formInitiation',
			handler: form.initiation
		},
		{
			selectorKey: 'typedSearchInit',
			handler: page.typedSearchInit
		}
	],
	change: [
		{
			selectorKey: 'formChange',
			handler: form.change
		}
	],
	focusin: [
		{
			selectorKey: 'formPaymentCalc',
			handler: form.paymentCalcInit
		}
	],
	submit: [
		{
			selectorKey: 'formSubmission',
			handler: form.submission
		}
	],
	dialogopen: [
		{
			handler: dialog.opened
		}
	],
	'show.bs.modal': [
		{
			handler: dialog.opened
		}
	],
	slideOpen: [
		{
			handler: dialog.opened
		},
		{
			// When dialog/slidein is open and ready to interact with
			handler: dialog.ajaxContentLoaded
		},
		{
			// Capture forms on dynamically loaded content
			// ajaxDialogShown: htdocs/v9/media/js/ddc/v1/modules/dialog.js
			handler: handleDynamicForms.bind(undefined, win)
		},
		{
			// US2055082: bind the sidebar submenu toggles in the opened slide-in.
			handler: rebindSidebarSubmenuToggles(win, link)
		}
	],
	dialogclose: [
		{
			handler: dialog.closed
		},
		{
			// // Strip out the `initiatingClickText` when a dialog is closed without being submitted
			handler: () =>
				safeLocalStorage.removeItem(
					'ddc_data_layer-initiatingClickText'
				)
		}
	],
	'hide.bs.modal': [
		{
			handler: dialog.closed
		}
	],
	'hide.bs.dropdown': [
		{
			// TA4137176: desktop nav megamenu dismissed without clicking its own
			// toggle (outside click / focused ESC) — see navDropdownHide above.
			handler: navDropdownHide
		}
	],
	'show.bs.dropdown': [
		{
			// TA4137176: opening one megamenu closes another with no bootstrap
			// event of its own — emit that close here. See navDropdownShow above.
			handler: navDropdownShow
		}
	],
	slideClose: [
		{
			handler: dialog.closed
		}
	],
	slidechange: [
		{
			handler: form.slideChange
		}
	],
	tabsshow: [
		{
			// Handle click events for jquery-ui tabs, because it doesn't bubble the normal event.
			handler: function handler(e, ui) {
				let eventObject;
				// import to note using regular function here and not arrow function as link.clicked needs 'this'
				if (ui?.tab) {
					eventObject = link.clicked.call(this, ui.tab, e);
				}
				return eventObject;
			}
		}
	],
	set: [
		{
			selectorKey: 'searchFacetVlpSlider',
			handler: filterSearchClick.bind(
				undefined,
				DDC_INVENTORY_FILTERSEARCH_CLICK
			)
		}
	],
	ajaxDialogComplete: [
		{
			// When dialog/slidein is open and ready to interact with
			handler: dialog.ajaxContentLoaded
		},
		{
			// Capture forms on dynamically loaded content
			// ajaxDialogShown: htdocs/v9/media/js/ddc/v1/modules/dialog.js
			handler: handleDynamicForms.bind(undefined, win)
		},
		{
			// US2055082: bind the sidebar submenu toggles in the opened slide-in.
			handler: rebindSidebarSubmenuToggles(win, link)
		}
	],
	hproductClick: [
		{
			handler: link.hproduct
		}
	],
	slideshowPagerClick: [
		{
			handler: slideshow.pagerClick
		}
	],
	videoGalleryFacetBrowseChange: [
		{
			// this handles video gallery facet browse changes coming from v9/widgets/video/gallery/v2/js/widget.js
			// not sure why the $pageBody.on('change'... below is not noticing the DOM element changes
			handler: (_event, originalEvent) => form.change(originalEvent)
		}
	],
	'ddc-mega-menu-nav-ajax-load-complete': [
		{
			// Capture forms on dynamically loaded content
			// ajaxDialogShown: htdocs/v9/media/js/ddc/v1/modules/dialog.js
			handler: handleDynamicForms.bind(undefined, win)
		},
		{
			// "Temporary" (5 year old) solution for skipping event.preventDefault which sometime triggers from the
			// Adobe Tag Manager script causing mega menu forms to be submitted twice. :-(
			handler: ({ target }) => {
				$(target).on(
					'click submit',
					getSelectorsToHandle(win).formSubmission,
					async (event) => {
						const eventObjects = await form.submission(event);
						eventObjects?.forEach((eventObject) => {
							pushEvent(win, eventObject);
						});
					}
				);
			}
		}
	],
	ajaxDialogShown: [
		{
			// Capture forms on dynamically loaded content
			// ajaxDialogShown: htdocs/v9/media/js/ddc/v1/modules/dialog.js
			handler: handleDynamicForms.bind(undefined, win)
		}
	],
	ddcEvent: [
		{
			/*
			Generic listener so we can call DDC Events from other places.
			Must provide the eventType in the ddcEventData object.
			ex.
			{
				eventType: DDC.dataLayer.events.form.confirmation
			}

			we probably want to update this so that additional arguments can be
			passed to the called functions
		*/
			handler: handleDDCEvent
		}
	],
	trackEvent: [
		{
			// handles trackEvent triggers so that we can use that data for generating DDC events
			handler: handleTrackEvent.bind(undefined, win)
		}
	]
});

export default getDocumentOnHandlers;
